import { lazy, Suspense } from 'react';
import { BodyCopy, Heading03 } from '@uds/typography';
import { Badge, Kind as BadgeKind } from '@uds/badge';
import Colorway, { Colorways } from '@uds/colorway';
import RenderCMSText from 'components/common/RenderCmsText/RenderCmsText';
import Price from 'presentation/components/common/Price';

import PurchaseGroup from '../../domain-search-result-hero/available/purchase/PurchaseGroup';
import { getClassNames } from './DomainSearchSuggestion.stylesheet';
import { ActionFeedbackKind } from '../../domain-search-result-hero/available/action-feedback/ActionFeedback';
import useDomainSearchSuggestions, { DomainSearchSuggestionProps } from './useDomainSearchSuggestion';

const LegalText = lazy(() => import('./LegalText'));

const DomainSearchSuggestion = (props: DomainSearchSuggestionProps) => {
  const {
    models: {
      parsedPremiumLink,
      domainNameOnly,
      domainExtensionOnly,
      firstYearPrice,
      oldFirstYearPrice,
      currency,
      hidePrices,
      domainAvailabilityStatus,
      hasPremiumBadge,
      hasAdBadge,
      hasSalePromoBadge,
      hasSaleSavingsText,
      hasCaveat,
      hasPremiumCaveat,
      pricingStrategy,
    },
    operations: { handleAddToBasket, handleViewBasket },
  } = useDomainSearchSuggestions(props);
  const { text } = props;

  const classNames = getClassNames({
    oldFirstYearPrice,
    firstYearPrice,
    hidePrices,
  });

  const difference = oldFirstYearPrice! - firstYearPrice!;
  const saving = Math.ceil((difference / oldFirstYearPrice!) * 100);
  return (
    <li className={classNames.rootWrapper}>
      <Colorway colorway={Colorways.LIGHT} className={classNames.root}>
        <div className={classNames.domainNameWrapper}>
          {(hasPremiumBadge || hasAdBadge) && (
            <div className={classNames.badgesWrapper}>
              {hasPremiumBadge && <Badge kind={BadgeKind.PRIMARY}>{props.text.premium.badgeLabel}</Badge>}
              {hasAdBadge && <Badge kind={BadgeKind.MUTED}>{props.text.ad.badgeLabel}</Badge>}
            </div>
          )}
          <Heading03>
            {domainNameOnly}
            <span className={classNames.domainNameExtension}>
              {'.'}
              {domainExtensionOnly}
            </span>
          </Heading03>
        </div>
        <div className={classNames.ctaWrapper}>
          {!hidePrices && (
            <div className={classNames.priceAndBadgeWrapper}>
              {hasSalePromoBadge && (
                <span className={classNames.saleBadge}>
                  <Badge kind={BadgeKind.HIGHLIGHT}>{props.text.domain.sale?.badge}</Badge>
                </span>
              )}
              {hasSaleSavingsText && (
                <Heading03 component="span" className={classNames.price}>
                  <RenderCMSText
                    text={props.text.domain.sale?.savings}
                    values={{
                      percentage: saving,
                    }}
                  />
                </Heading03>
              )}
              <BodyCopy className={classNames.captionPrice}>
                <span className={classNames.priceWrap}>
                  <Heading03 component="span" className={classNames.price}>
                    <RenderCMSText
                      text={text.domain.price}
                      values={{
                        symbol: hasSalePromoBadge ? '*' : '',
                        price: (
                          <span className={classNames.pricePerYear}>
                            <Price amount={firstYearPrice!} currency={currency!} pricingStrategy={pricingStrategy} />
                          </span>
                        ),
                      }}
                    />
                  </Heading03>
                  {(hasCaveat || hasPremiumCaveat) && <span className={classNames.pricePerYear}>{'*'}</span>}
                </span>
                {oldFirstYearPrice != null && (
                  <span className={classNames.priceBefore}>
                    <RenderCMSText
                      text={text.domain.recurringPrice}
                      values={{
                        oldprice: <Price amount={oldFirstYearPrice} currency={currency!} pricingStrategy={pricingStrategy} />,
                      }}
                    />
                  </span>
                )}
              </BodyCopy>
            </div>
          )}
          <div className={classNames.purchaseGroup}>
            <PurchaseGroup
              feedbackKind={ActionFeedbackKind.SECONDARY_SUCCESS}
              unavailableFeedbackKind={ActionFeedbackKind.LIGHT}
              availability={domainAvailabilityStatus}
              text={{
                addToBasket: props.text.domain.CTA.label,
                basket: props.text.checkout.CTA.label,
                addedToBasket: props.text.checkout.label.addedState,
                unavailable: props.text.checkout.label.unavailableState,
                verifyingGlyph: text.checkout.glyph.verificationState,
                addedGlyph: text.checkout.glyph.addedState,
                basketGlyph: text.checkout.CTA.glyph,
                purchaseGlyph: text.domain.CTA.glyph,
                unavailableGlyph: text.checkout.glyph.unavailableState,
              }}
              actions={{
                handleAddToBasket,
                handleViewBasket,
              }}
            />
          </div>
        </div>
      </Colorway>
      {(hasCaveat || hasPremiumCaveat) && (
        <Suspense fallback={null}>
          <LegalText
            legalText={props.item.legalText}
            hasCaveat={hasCaveat}
            hasPremiumCaveat={hasPremiumCaveat}
            colorway={props.colorway}
            parsedPremiumLink={parsedPremiumLink}
          />
        </Suspense>
      )}
    </li>
  );
};

export default DomainSearchSuggestion;